import { useState, type CSSProperties, type ReactNode } from "react";
import { Avatar, Button, EmptyState, Select, vars } from "@pack-it-up/ui-web";
import type { MemberEntry, Note, NotesViewConfig } from "@pack-it-up/core";
import type { FractionalIndex, UserId } from "@pack-it-up/shared";
import { DndContext, DragOverlay, closestCenter, type DragStartEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { NoteCard } from "./NoteCard.js";
import { SearchField } from "./SearchField.js";
import { TagField } from "./TagField.js";
import { Sortable } from "../../features/dnd/Sortable.js";
import { useDndSensors } from "../../features/dnd/sensors.js";
import { makeNoteDragEnd } from "../../features/dnd/dropHandlers.js";
import { DragDisabledHint } from "../../features/dnd/DragDisabledHint.js";
import { DragPreview } from "../../features/dnd/DragPreview.js";

// Notes tab (Doc 05 §6): authored feed — composer at top, then a filter bar (tag +
// sort), then the notes themselves. Sort "manual" is drag-reorderable (new notes
// prepend to the top, same spot the old fixed newest-first sort always put them);
// "dateAdded" is the old fixed newest-first order. Per-note delete confirm dialog.
// URLs typed into the body are auto-linkified, not added separately.
export const NotesTab = ({
  notes,
  canEdit,
  currentUserId,
  members,
  view,
  onViewChange,
  tags = [],
  tagPalette = [],
  onAdd,
  onUpdate,
  onUpdateTags,
  onReorder,
  onRemove,
}: {
  // Pre-filtered and pre-sorted by the caller (tripDetailStore.visibleNotes()) — this
  // component only renders the given order, it doesn't compute one.
  notes: Note[];
  canEdit: boolean;
  /** The signed-in user's id — used to label own notes "(you)" and for the composer avatar. */
  currentUserId: UserId | undefined;
  /** Trip members keyed by participant id — used to resolve author display names. */
  members: Record<string, MemberEntry>;
  view: NotesViewConfig;
  onViewChange: (patch: Partial<NotesViewConfig>) => void;
  // Tags in use across the whole trip/template (T29, own + auto) — the filter
  // dropdown's options, same convention as the packing/to-do Toolbar's `tags` prop.
  tags?: string[];
  /** The composer/edit tag field's one-click palette — own tags only (issue #180). */
  tagPalette?: string[];
  onAdd: (body: string, tags?: string[]) => Promise<boolean>;
  onUpdate: (noteId: string, body: string) => void;
  onUpdateTags: (noteId: string, tags: string[]) => void;
  onReorder: (noteId: string, order: FractionalIndex) => void;
  onRemove: (noteId: string) => void;
}): ReactNode => {
  const [draft, setDraft] = useState("");
  const [draftTags, setDraftTags] = useState<string[]>([]);

  const isManual = view.sort === "manual";
  const tagValue = view.filterTags[0] ?? "all";

  const myName =
    currentUserId !== undefined
      ? (members[currentUserId]?.profile.displayName ?? "You")
      : "You";

  const composerRow: CSSProperties = {
    display: "flex",
    gap: vars.space.sm,
    alignItems: "flex-start",
    marginBottom: vars.space.lg,
  };

  const composerBody: CSSProperties = {
    flex: 1,
    minWidth: 0,
    background: vars.color.surface,
    border: `1px solid ${vars.color.border}`,
    borderRadius: vars.radius.md,
    padding: `12px 14px`,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  };

  const composerTextarea: CSSProperties = {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "64px",
    padding: 0,
    border: "none",
    background: "transparent",
    color: vars.color.text,
    font: "inherit",
    fontSize: vars.font.sizeSm,
    resize: "vertical",
    outline: "none",
  };

  const sensors = useDndSensors();
  const handleDragEnd = makeNoteDragEnd(notes, (noteId, order) => onReorder(noteId, order));
  // The dragged card's own node sits inside a SortableContext whose `items` only
  // catches up once the reorder round-trips through the note store and its stream —
  // without a ghost to mask that gap, dropping snaps the card back to its old slot
  // and then jumps to the new one a beat later. Same fix as PackingList/TodoList rows:
  // a DragOverlay ghost with dropAnimation off (no flight path back to a stale rect).
  const [draggingNote, setDraggingNote] = useState<Note | null>(null);
  const handleDragStart = (event: DragStartEvent): void =>
    setDraggingNote(notes.find((n) => n.id === event.active.id) ?? null);

  return (
    <div>
      {canEdit && (
        <div style={composerRow}>
          <Avatar name={myName} size="sm" />
          <div style={composerBody}>
            <textarea
              aria-label="New note"
              placeholder="Share a note with the group — a reminder, a link, a packing tip…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              style={composerTextarea}
            />
            <div style={{ display: "flex", alignItems: "flex-end", gap: vars.space.sm }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <TagField value={draftTags} palette={tagPalette} onChange={setDraftTags} />
              </div>
              <Button
                disabled={draft.trim() === ""}
                onClick={() => {
                  void (async () => {
                    // Only clear the composer once the note is actually saved — clearing
                    // unconditionally made a failed post (e.g. offline, permission error)
                    // look identical to a successful one.
                    const ok = await onAdd(draft.trim(), draftTags);
                    if (ok) {
                      setDraft("");
                      setDraftTags([]);
                    }
                  })();
                }}
              >
                Post note
              </Button>
            </div>
          </div>
        </div>
      )}
      {canEdit && <div style={{ borderTop: `1px solid ${vars.color.border}`, margin: `0 0 ${vars.space.lg}` }} />}
      <div style={{ display: "flex", alignItems: "center", gap: vars.space.sm, flexWrap: "wrap", marginBottom: vars.space.md }}>
        <SearchField
          value={view.query}
          onChange={(query) => onViewChange({ query })}
          placeholder="Search notes…"
        />
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto", flexWrap: "wrap" }}>
          <Select
            compact
            label="Sort"
            value={view.sort}
            onChange={(v) => onViewChange({ sort: v as NotesViewConfig["sort"] })}
            options={[
              { value: "manual", label: "Sort: Manual" },
              { value: "dateAdded", label: "Sort: Date added" },
            ]}
          />
          {tags.length > 0 && (
            <Select
              compact
              label="Tag"
              value={tagValue}
              onChange={(v) => onViewChange({ filterTags: v === "all" ? [] : [v] })}
              options={[{ value: "all", label: "Tag: All" }, ...tags.map((t) => ({ value: t, label: `Tag: ${t}` }))]}
            />
          )}
        </div>
      </div>
      {!isManual && canEdit && <DragDisabledHint />}
      {notes.length === 0 ? (
        <EmptyState
          title="No notes yet"
          description="Notes are a great way to share reminders, links, or tips with your travel group."
          icon="📝"
        />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={(event) => {
            handleDragEnd(event);
            setDraggingNote(null);
          }}
          onDragCancel={() => setDraggingNote(null)}
        >
          <SortableContext items={notes.map((n) => n.id)} strategy={verticalListSortingStrategy} disabled={!isManual}>
            {notes.map((n) => {
              const authorName = members[n.authorId]?.profile.displayName ?? "Unknown";
              return (
                <Sortable key={n.id} id={n.id} disabled={!isManual || !canEdit}>
                  {(handleProps) => (
                    <NoteCard
                      note={n}
                      authorName={authorName}
                      isOwn={n.authorId === currentUserId}
                      canEdit={canEdit}
                      {...(isManual && canEdit ? { dragHandleProps: handleProps } : {})}
                      onUpdate={(body) => onUpdate(n.id, body)}
                      onUpdateTags={(tags) => onUpdateTags(n.id, tags)}
                      tagPalette={tagPalette}
                      onRemove={() => onRemove(n.id)}
                    />
                  )}
                </Sortable>
              );
            })}
          </SortableContext>
          <DragOverlay dropAnimation={null}>
            {draggingNote !== null && <DragPreview name={draggingNote.body} />}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
};
